import { GlobalStore } from '@highlight/ipc'

/**
 * Only items created within this window are treated as genuine new arrivals.
 * An unseen item older than this is a backlog entry resurfacing (a refetch /
 * cross-window cache-sync / a page backfilling after a delete / a reset
 * seen-set), not news.
 */
export const NEW_ITEM_MAX_AGE_MS = 5 * 60 * 1000

/** Each announcing surface keeps its own memory of what it has already said. */
export type AnnouncedItemKind = 'stagedAction' | 'reminder' | 'autoResolvedReminder'

const ANNOUNCED_IDS_KEYS: Record<AnnouncedItemKind, string> = {
  stagedAction: 'toastedStagedActionIds',
  reminder: 'toastedReminderIds',
  autoResolvedReminder: 'toastedAutoResolvedReminderIds',
}

/**
 * Bound on each persisted set. Far more than the items in play at once, small
 * enough that the whole list stays a cheap store read.
 */
const MAX_ANNOUNCED_IDS = 500

export interface AnnounceableItem {
  id: string
  createdAt: string
}

export interface FreshItemsInput<T extends AnnounceableItem> {
  items: Array<T>
  /** Ids this window instance has already observed in the list. */
  seenIds: Set<string>
  /** Ids the user has already been toasted about, in any window or session. */
  announcedIds: Set<string>
  now: number
  /** Already dealt with by the user — an archived action, a dismissed reminder. */
  isRetired?: (item: T) => boolean
}

/**
 * The items worth announcing out of a raw list pass.
 *
 * A list query is not a feed of new things — it's the user's backlog, replayed
 * on every refetch and cross-window sync, and rows re-enter it for reasons that
 * have nothing to do with news:
 *
 * - Archived staged actions stay in the list (the server returns everything not
 *   deleted, and archiving touches neither `createdAt` nor `metadata.readAt`),
 *   and editing one deliberately clears `readAt` so its unread dot returns.
 * - The reminders list is a capped page: deleting one frees a slot, so the next
 *   refetch backfills an older reminder this window has never seen.
 *
 * Hence the gates below. `seenIds` alone can't carry them: it is per window
 * instance, and there are two (overlay + chat window), each remounting and
 * reseeding on its own schedule.
 */
export function selectFreshItems<T extends AnnounceableItem>({
  items,
  seenIds,
  announcedIds,
  now,
  isRetired,
}: FreshItemsInput<T>): Array<T> {
  return items.filter((item) => {
    // Dealt with is never news, however the row re-entered the list.
    if (isRetired?.(item)) return false
    // Announced once, never again — an edit clearing `readAt` must not undo this.
    if (announcedIds.has(item.id)) return false
    if (seenIds.has(item.id)) return false
    const createdMs = new Date(item.createdAt).getTime()
    return isFinite(createdMs) && now - createdMs <= NEW_ITEM_MAX_AGE_MS
  })
}

/**
 * Reminders Highlight resolved on its own that the user has not been told
 * about yet. Keyed on when Highlight resolved them, not on creation. The
 * reminder is old news, the check-off is not.
 */
export function selectFreshAutoResolutions<T extends { id: string }>({
  items,
  resolvedAtOf,
  announcedIds,
  now,
  isRetired,
}: {
  items: Array<T>
  /** When Highlight resolved the item, or null when it didn't. */
  resolvedAtOf: (item: T) => string | null
  announcedIds: Set<string>
  now: number
  /** Already dealt with by the user, such as a dismissed reminder. */
  isRetired?: (item: T) => boolean
}): Array<T> {
  return items.filter((item) => {
    if (isRetired?.(item)) return false
    if (announcedIds.has(item.id)) return false
    const at = resolvedAtOf(item)
    const atMs = at ? new Date(at).getTime() : NaN
    return isFinite(atMs) && now - atMs <= NEW_ITEM_MAX_AGE_MS
  })
}

export interface FlushableSuppressedInput<T extends AnnounceableItem> {
  /** Items held back while they were visible inline, in detection order. */
  pending: Array<T>
  /** The freshest fetched row per id — held items are re-validated against it. */
  latestById: Map<string, T>
  /** Ids the user has already been toasted about, in any window or session. */
  announcedIds: Set<string>
  /** Already dealt with by the user — an archived action, a dismissed reminder. */
  isRetired?: (item: T) => boolean
  /** Durable server read-state — a read item must not announce. */
  isUnread: (item: T) => boolean
  max: number
}

/**
 * The held-back items still worth announcing when the surface that suppressed
 * them goes away (e.g. the sidepanel closes off an inline chat, HIG-6036).
 *
 * Each pending item already passed `selectFreshItems` at detection time; any
 * delay since is user-caused (the panel stayed open), so the age gate
 * deliberately does NOT re-apply here — only the durable gates that may have
 * flipped while the item was held: read, retired, announced by a peer window,
 * or gone from the list entirely.
 */
export function selectFlushableSuppressedItems<T extends AnnounceableItem>({
  pending,
  latestById,
  announcedIds,
  isRetired,
  isUnread,
  max,
}: FlushableSuppressedInput<T>): Array<T> {
  const flushable: Array<T> = []
  for (const stale of pending) {
    if (flushable.length >= max) break
    const latest = latestById.get(stale.id)
    if (!latest) continue
    if (isRetired?.(latest)) continue
    if (announcedIds.has(latest.id)) continue
    if (!isUnread(latest)) continue
    flushable.push(latest)
  }
  return flushable
}

/** Ids already announced for this kind, persisted per user and shared across windows. */
export function loadAnnouncedIds(kind: AnnouncedItemKind): Set<string> {
  return new Set(GlobalStore.user().get<Array<string>>(ANNOUNCED_IDS_KEYS[kind], []))
}

/** Record that the user has been toasted about this item. FIFO-capped. */
export function rememberAnnounced(kind: AnnouncedItemKind, id: string): void {
  GlobalStore.user().set<Array<string>>(ANNOUNCED_IDS_KEYS[kind], (prev) => {
    const next = (prev ?? []).filter((existing) => existing !== id)
    next.push(id)
    return next.length > MAX_ANNOUNCED_IDS ? next.slice(-MAX_ANNOUNCED_IDS) : next
  })
}
